import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Icon } from "@simplybusiness/mobius";
import { sparkles } from "@simplybusiness/icons";
import { ChatbotPrompt } from "./ChatbotPrompt";
import { type ScrollDirection, ScrollButton } from "./ScrollButton";
import { useInsuranceAdvisor } from "../../../../../../../../contexts";
import { isQuestion } from "../../../../types";
import { useQuestionnaire } from "../../../../../../../../features/questionnaire";
import "./ChatbotPrompts.css";

export type ChatbotPromptsProps = {
  chatbotPrompts: string[];
  productCode?: string;
};

export const ChatbotPrompts = ({
  chatbotPrompts,
  productCode,
}: ChatbotPromptsProps) => {
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { getAdvisorAttributes } = useInsuranceAdvisor();

  const { sections, answers } = useQuestionnaire();

  const primaryTrade = useMemo(() => {
    for (const section of sections) {
      const question = section.questions
        .filter(isQuestion)
        .find(q => q.trackAs === "primary_detail_selected");
      if (question) {
        return answers[question.name] as string | undefined;
      }
    }
    return undefined;
  }, [sections, answers]);

  const advisorAttributes = getAdvisorAttributes(
    productCode ?? "",
    primaryTrade ? { "data-trade": primaryTrade } : undefined,
  );

  // Observe the first and last pills to determine scroll button visibility
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const pills = Array.from(container.children) as HTMLElement[];
    const first = pills[0];
    const last = pills[pills.length - 1];
    if (!first || !last) return;

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.target === first) setAtStart(entry.isIntersecting);
          if (entry.target === last) setAtEnd(entry.isIntersecting);
        }
      },
      { root: container, threshold: 0.99 },
    );

    observer.observe(first);
    observer.observe(last);
    return () => observer.disconnect();
  }, []);

  const isScrollable = !atStart || !atEnd;

  const handleScrollClick = useCallback((direction: ScrollDirection) => {
    const container = scrollRef.current;
    if (!container) return;

    const pills = Array.from(container.children) as HTMLElement[];
    const containerRect = container.getBoundingClientRect();

    if (direction === "right") {
      const target = pills.find(
        p => p.getBoundingClientRect().right > containerRect.right,
      );
      target?.scrollIntoView({
        behavior: "smooth",
        inline: "start",
        block: "nearest",
      });
    } else {
      const target = [...pills]
        .reverse()
        .find(p => p.getBoundingClientRect().left < containerRect.left);
      target?.scrollIntoView({
        behavior: "smooth",
        inline: "end",
        block: "nearest",
      });
    }
  }, []);

  return (
    <div className="mobius-chatbot-prompts" aria-label="Chatbot prompts">
      <Icon
        className={!atStart ? "--is-hidden" : ""}
        icon={sparkles}
        size="md"
        data-testid="sparkles-icon"
        title="AI-powered chatbot prompts"
      />
      {isScrollable && (
        <ScrollButton
          onClick={handleScrollClick}
          isHidden={atStart}
          direction="left"
        />
      )}
      <div
        ref={scrollRef}
        className="mobius-chatbot-prompts__scroll"
        role="list"
      >
        {chatbotPrompts.map(prompt => (
          <div key={prompt} role="listitem">
            <ChatbotPrompt
              label={prompt}
              advisorAttributes={advisorAttributes}
            />
          </div>
        ))}
      </div>
      {isScrollable && (
        <ScrollButton
          onClick={handleScrollClick}
          isHidden={atEnd}
          direction="right"
        />
      )}
    </div>
  );
};
